'use client';

import { JSX, useEffect, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import {
  formatToHourAndMinutes,
  formatToWeekdayPlusHourAndMinutes,
  getMinutesUntil,
  isToday,
} from '@bts-web/utils-formatting';
import { MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS } from '@bts-web/core-features/config';
import { truncateAssetName } from '@bts-web/utils-functions';
import { Drawer } from '../../../../../common/components/Drawer';
import { HTMLRenderer } from '../../../../../common/components/HTMLRenderer/HTMLRenderer';
import { FilledSunrise } from '../../../../../common/svgIcons';
import { MoonIcon } from '../MarketStatusIcon/MoonIcon';
import { SmallMoonIcon } from '../MarketStatusIcon/SmallMoonIcon';
import { SunIcon } from '../MarketStatusIcon/SunIcon';
import { SunsetIcon } from '../MarketStatusIcon/SunsetIcon';
import { SessionRow } from './SessionRow';

interface MarketHoursDrawerProps {
  assetName: string;
  marketClosingAt?: Date;
  marketClosedUntil?: Date;
  lastOpenTime?: Date;
  lastCloseTime?: Date;
  locale: string;
}

const MAX_ASSET_NAME_LENGTH = 25;

const MarketHoursDrawer = ({
  assetName,
  marketClosingAt,
  marketClosedUntil,
  lastOpenTime,
  lastCloseTime,
  locale,
}: MarketHoursDrawerProps): JSX.Element => {
  const { t } = useClientTranslation();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);

    return () => clearInterval(id);
  }, []);

  const minutesUntilClose = marketClosingAt
    ? getMinutesUntil(marketClosingAt, now)
    : null;

  const minutesUntilOpen = marketClosedUntil
    ? getMinutesUntil(marketClosedUntil, now)
    : null;

  const getTopMarketStatus = (): {
    icon: JSX.Element | null;
    text: string;
  } => {
    if (
      marketClosingAt &&
      minutesUntilClose !== null &&
      minutesUntilClose > 0 &&
      minutesUntilClose <=
        MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS
    ) {
      const text = t('stock_market_closes_in', { minutes: minutesUntilClose });

      return { text, icon: <SunsetIcon ariaLabel={text} /> };
    }

    if (marketClosingAt) {
      return {
        text: t('market_hours_trading_available', {
          time: formatToHourAndMinutes(marketClosingAt, locale),
        }),
        icon: <FilledSunrise size={20} color="brand.fill_primary" />,
      };
    }

    if (
      marketClosedUntil &&
      minutesUntilOpen !== null &&
      minutesUntilOpen > 0 &&
      minutesUntilOpen <=
        MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS
    ) {
      const text = t('stock_market_opens_in', { minutes: minutesUntilOpen });

      return {
        text,
        icon: (
          <SmallMoonIcon colorToken="warning.fill_primary" ariaLabel={text} />
        ),
      };
    }

    if (marketClosedUntil) {
      const text = t('stock_market_closed_until', {
        date: formatToWeekdayPlusHourAndMinutes(marketClosedUntil, locale),
      });

      return {
        text,
        icon: (
          <SmallMoonIcon colorToken="brand.fill_primary" ariaLabel={text} />
        ),
      };
    }

    return { icon: null, text: '' };
  };

  const { icon: topMarketStatusIcon, text: topMarketStatusText } =
    getTopMarketStatus();

  const isMarketOpen = !!marketClosingAt;

  // Regular hours time range, displayed as "HH:MM - HH:MM"
  const regularMarketHoursFormattedOpeningTime = lastOpenTime
    ? formatToHourAndMinutes(lastOpenTime, locale)
    : null;

  const regularMarketHoursFormattedClosingTime =
    isMarketOpen && marketClosingAt
      ? formatToHourAndMinutes(marketClosingAt, locale)
      : lastCloseTime
        ? formatToHourAndMinutes(lastCloseTime, locale)
        : null;

  const intervalDuringWhichMarketIsOpen =
    regularMarketHoursFormattedOpeningTime &&
    regularMarketHoursFormattedClosingTime
      ? `${regularMarketHoursFormattedOpeningTime} – ${regularMarketHoursFormattedClosingTime}`
      : null;

  // Market closed time range, displayed as "HH:MM - HH:MM", or "Weekday, HH:MM - Weekday, HH:MM"
  const closedMarketHoursFormattedLastClosingTime = lastCloseTime
    ? isToday(lastCloseTime, now)
      ? formatToHourAndMinutes(lastCloseTime, locale)
      : formatToWeekdayPlusHourAndMinutes(lastCloseTime, locale)
    : null;

  const intervalDuringWhichMarketIsClosed =
    closedMarketHoursFormattedLastClosingTime && marketClosedUntil
      ? `${closedMarketHoursFormattedLastClosingTime} – ${formatToWeekdayPlusHourAndMinutes(marketClosedUntil, locale)}`
      : marketClosedUntil
        ? formatToWeekdayPlusHourAndMinutes(marketClosedUntil, locale)
        : null;

  // Icons per session: colored when active, muted when not
  const regularIcon: JSX.Element = (
    <SunIcon active={isMarketOpen} ariaLabel={t('market_session_regular')} />
  );

  const isMarketAboutToOpen =
    !isMarketOpen &&
    minutesUntilOpen !== null &&
    minutesUntilOpen > 0 &&
    minutesUntilOpen <=
      MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS;

  const closedIcon: JSX.Element = (
    <MoonIcon
      active={!isMarketOpen}
      activeColorToken={
        isMarketAboutToOpen ? 'warning.fill_primary' : undefined
      }
      ariaLabel={t('market_session_market_closed')}
    />
  );

  return (
    <Drawer title={t('market_hours_drawer_title')}>
      {/* Asset name + live status */}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'extra_small_2',
          paddingX: 'medium',
          paddingTop: 'medium',
          paddingBottom: 'small',
          alignItems: 'center',
        })}
      >
        <span
          className={css({
            fontSize: 'headline.large_semi_bold',
            fontWeight: 'headline.large_semi_bold',
            letterSpacing: 'headline.large_semi_bold',
            lineHeight: 'headline.large_semi_bold',
            textAlign: 'center',
          })}
        >
          {truncateAssetName(assetName, MAX_ASSET_NAME_LENGTH)}
        </span>

        {topMarketStatusText && (
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: 'extra_small_2',
            })}
          >
            {topMarketStatusIcon}
            <span
              className={css({
                fontSize: 'body.medium_medium',
                fontWeight: 'body.medium_medium',
                letterSpacing: 'body.medium_medium',
                lineHeight: 'body.medium_medium',
                color: 'neutrals.text_secondary',
                '& [data-element="markdown-style"]': {
                  color: 'neutrals.text_primary',
                },
              })}
            >
              <HTMLRenderer>{topMarketStatusText}</HTMLRenderer>
            </span>
          </div>
        )}
      </div>

      {/* Session rows */}
      <div
        className={css({
          paddingX: 'medium',
          backgroundColor: 'neutrals.widget_primary',
          mx: 'medium',
          borderRadius: '20px',
        })}
      >
        <SessionRow
          active={isMarketOpen}
          icon={regularIcon}
          label={t('market_session_regular')}
          timeRange={intervalDuringWhichMarketIsOpen}
          description={t('market_session_regular_description')}
        />

        <SessionRow
          active={!isMarketOpen}
          icon={closedIcon}
          label={t('market_session_market_closed')}
          timeRange={intervalDuringWhichMarketIsClosed}
          description={t('market_session_market_closed_description')}
        />
      </div>

      {/* Footer disclaimer */}
      <p
        className={css({
          fontSize: 'caption.small',
          fontWeight: 'caption.small',
          letterSpacing: 'caption.small',
          lineHeight: 'caption.small',
          color: 'neutrals.text_secondary',
          padding: 'medium',
          textAlign: 'center',
        })}
      >
        {t('market_hours_timezone_disclaimer')}
      </p>
    </Drawer>
  );
};

export { MarketHoursDrawer };
